/* @ngInject */

export default ($rootScope, helperService, helperPdfService, PaymentFrequency) => {
  /**
   * Prepare asset finance preferred product details.
   *
   * @param {Array} lenders
   * @param {Object} preferredProduct
   * @param {Object} loan
   *
   * @returns {Object}
   */
  const prepareAssetFinancePreferredProductDetails = ({ lenders, preferredProduct, loan }) => {
    const content = [];
    content.push(
      helperPdfService.createHeader({
        title: 'PROPOSED BORROWING STRUCTURE'
      })
    );
    let count = 0;
    const amortizationScheduleCounts = [];
    for (let i = 0; i < loan.financeProposal.amortizationSchedules.length; i += 1) {
      if (count === 0) {
        amortizationScheduleCounts.push({
          repaymentDate: loan.financeProposal.amortizationSchedules[i].getPaymentDate(),
          numberOfPayments: count + 1,
          repayment: loan.financeProposal.amortizationSchedules[i].repayment
        });
      }
      if (i + 1 <= loan.financeProposal.amortizationSchedules.length - 1) {
        if (
          loan.financeProposal.amortizationSchedules[i].repayment !==
          loan.financeProposal.amortizationSchedules[i + 1].repayment
        ) {
          amortizationScheduleCounts[amortizationScheduleCounts.length - 1].numberOfPayments =
            count + 1;
          amortizationScheduleCounts[amortizationScheduleCounts.length - 1].repayment =
            loan.financeProposal.amortizationSchedules[i].repayment;
          count = 0;
        } else {
          count += 1;
        }
      } else {
        amortizationScheduleCounts[amortizationScheduleCounts.length - 1].numberOfPayments =
          count + 1;
      }
    }
    content.push({
      table: {
        widths: ['25%', '25%', '25%', '25%'],
        body: [
          [
            {
              text: [
                { text: 'Lender\n', style: 'label' },
                {
                  text: helperService.getLenderByContentType({
                    lenders,
                    lenderId: preferredProduct?.selectedProduct?.lender[0]?.id,
                    contentType: 'id'
                  }),
                  style: 'value'
                }
              ],
              colSpan: 2
            },
            '',
            {
              text: [
                { text: 'Product\n', style: 'label' },
                {
                  text: preferredProduct?.selectedProduct?.name || '-',
                  style: 'value'
                }
              ],
              colSpan: 2
            },
            ''
          ],
          [
            {
              text: [
                { text: 'Net amount financed\n', style: 'label' },
                {
                  text: loan?.financeProposal?.netAmountFinanced.formatWithCurrency(
                    $rootScope.CurrentCurrency(),
                    2
                  ),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Term (months)\n', style: 'label' },
                {
                  text: helperService.getTitleOfSelectedTermInMonths({
                    term: loan?.financeProposal?.term
                  }),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Base interest rate\n', style: 'label' },
                {
                  text: `${loan?.financeProposal?.interestRate.toFixed(2)}%`,
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Interest rate\n', style: 'label' },
                {
                  text: `${loan?.financeProposal?.estimatedBorrowerRate.toFixed(2)}%`,
                  style: 'value'
                }
              ]
            }
          ],
          [
            {
              text: [
                {
                  text: `${
                    loan?.financeProposal?.productType === 2 ||
                    loan?.financeProposal?.productType === 3 ||
                    loan?.financeProposal?.productType === 7
                      ? 'Rental'
                      : 'Repayment'
                  }\n`,
                  style: 'label'
                },
                {
                  text:
                    amortizationScheduleCounts.length > 1
                      ? 'N/A'
                      : loan?.financeProposal?.estimatedRepayment.formatWithCurrency(
                          $rootScope.CurrentCurrency(),
                          2
                        ),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Upfront fee\n', style: 'label' },
                {
                  text: preferredProduct?.productDetails?.upfrontFee.formatWithCurrency(
                    $rootScope.CurrentCurrency(),
                    2
                  ),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Monthly fee\n', style: 'label' },
                {
                  text: preferredProduct?.repayments?.monthlyFee.formatWithCurrency(
                    $rootScope.CurrentCurrency(),
                    2
                  ),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Product type\n', style: 'label' },
                {
                  text: helperService.getAssetFinanceProductType(
                    loan?.financeProposal?.productType
                  ),
                  style: 'value'
                }
              ]
            }
          ],
          [
            {
              text: [
                { text: 'Balloon percent\n', style: 'label' },
                {
                  text: `${loan?.financeProposal?.balloonPercent.toFixed(2)}%` || '-',
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Balloon value\n', style: 'label' },
                {
                  text: loan?.financeProposal?.balloonValue.formatWithCurrency(
                    $rootScope.CurrentCurrency(),
                    2
                  ),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Payments\n', style: 'label' },
                {
                  text: loan?.financeProposal?.payments ? 'Advance' : 'Arrears',
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Payment frequency\n', style: 'label' },
                {
                  text:
                    loan?.financeProposal?.paymentFrequency?.name !== PaymentFrequency.none.name
                      ? loan?.financeProposal?.paymentFrequency?.title || '-'
                      : '-',
                  style: 'value'
                }
              ]
            }
          ]
        ]
      },
      layout: 'noBorders'
    });
    if (amortizationScheduleCounts.length > 1) {
      content.push({
        table: {
          widths: ['25%', '25%', '25%', '25%'],
          body: [
            [
              { text: 'Date', style: 'label' },
              { text: '# Payments', style: 'label' },
              {
                text: `${
                  loan?.financeProposal?.productType === 2 ||
                  loan?.financeProposal?.productType === 3 ||
                  loan?.financeProposal?.productType === 7
                    ? 'Rental'
                    : 'Repayment'
                }\n`,
                style: 'label'
              },
              ''
            ]
          ]
        },
        layout: 'noBorders'
      });
      const aSLen = amortizationScheduleCounts.length;
      for (let a = 0; a < aSLen; a += 1) {
        const amortizationScheduleCount = amortizationScheduleCounts[a];
        content[content.length - 1].table.body.push(['', '', '', '']);
        content[content.length - 1].table.body[
          content[content.length - 1].table.body.length - 1
        ][0] = {
          text: amortizationScheduleCount.repaymentDate,
          style: 'value'
        };
        content[content.length - 1].table.body[
          content[content.length - 1].table.body.length - 1
        ][1] = {
          text: amortizationScheduleCount.numberOfPayments,
          style: 'value'
        };
        content[content.length - 1].table.body[
          content[content.length - 1].table.body.length - 1
        ][2] = {
          text: amortizationScheduleCount.repayment.formatWithCurrency(
            $rootScope.CurrentCurrency(),
            2
          ),
          style: 'value'
        };
      }
    }
    return content;
  };

  /**
   * Prepare home loan preferred product details.
   *
   * @param {Array} lenders
   * @param {Object} preferredProduct
   *
   * @returns {Object}
   */
  const prepareHomeLoanPreferredProductDetails = ({ lenders, preferredProduct }) => {
    const organizationCountry = helperService.getOrganizationCountry();
    const content = [];
    content.push(
      helperPdfService.createHeader({
        title: 'RECOMMENDED PRODUCT'
      })
    );
    content.push({
      table: {
        widths: ['25%', '25%', '25%', '25%'],
        body: [
          [
            {
              text: [
                { text: 'Lender\n', style: 'label' },
                {
                  text: helperService.getLenderByContentType({
                    lenders,
                    lenderId: preferredProduct?.idLender,
                    contentType: 'id'
                  }),
                  style: 'value'
                }
              ],
              colSpan: 4
            },
            '',
            '',
            ''
          ]
        ]
      },
      layout: 'noBorders'
    });
    const len = preferredProduct?.productSplits?.length || 0;
    for (let p = 0; p < len; p += 1) {
      const productSplit = preferredProduct.productSplits[p];
      content.push(helperPdfService.createSubHeader({ title: `SPLIT #${p + 1}` }));
      content.push({
        table: {
          widths: ['25%', '25%', '25%', '25%'],
          body: [
            [
              {
                text: [
                  { text: 'Loan amount\n', style: 'label' },
                  {
                    text: productSplit?.totalLoanAmount.formatWithCurrency(
                      $rootScope.CurrentCurrency(),
                      0
                    ),
                    style: 'value'
                  }
                ]
              },
              {
                text: [
                  { text: 'Loan term\n', style: 'label' },
                  {
                    text:
                      productSplit?.loanTerm > 1
                        ? `${productSplit?.loanTerm} years`
                        : `${productSplit?.loanTerm} year`,
                    style: 'value'
                  }
                ]
              },
              {
                text: [
                  { text: 'Product name\n', style: 'label' },
                  {
                    text: productSplit?.productName || '-',
                    style: 'value'
                  }
                ],
                colSpan: 2
              },
              ''
            ]
          ]
        },
        layout: 'noBorders'
      });

      if (organizationCountry?.cca2 === 'NZ') {
        content[content.length - 1].table.body.push([
          {
            text: [
              { text: 'Interest rate\n', style: 'label' },
              {
                text: `${productSplit?.interestRate.toFixed(2)}%` || '-',
                style: 'value'
              }
            ]
          },
          {
            text: [
              {
                text: 'Lending purpose code\n',
                style: 'label'
              },
              {
                text: [
                  {
                    text: productSplit?.absLendingPurposeCode?.value || '-',
                    style: 'value'
                  }
                ]
              }
            ]
          },
          {
            text: [
              {
                text: 'Repayment 1\n',
                style: 'label'
              },
              {
                text: [
                  {
                    text: productSplit?.repayment1.formatWithCurrency(
                      $rootScope.CurrentCurrency(),
                      0
                    ),
                    style: 'value'
                  },
                  {
                    text: ` ${productSplit?.repayment1Info || '-'}`,
                    style: 'valueXS'
                  }
                ]
              }
            ]
          },
          ''
        ]);
      } else {
        content[content.length - 1].table.body.push([
          {
            text: [
              { text: 'Interest rate\n', style: 'label' },
              {
                text: `${productSplit?.interestRate.toFixed(2)}%` || '-',
                style: 'value'
              }
            ]
          },
          {
            text: [
              { text: 'Revert rate\n', style: 'label' },
              {
                text: `${productSplit?.revertRate.toFixed(2)}%` || '-',
                style: 'value'
              }
            ]
          },
          {
            text: [
              {
                text: 'Lending purpose code\n',
                style: 'label'
              },
              {
                text: [
                  {
                    text: productSplit?.absLendingPurposeCode?.value || '-',
                    style: 'value'
                  }
                ]
              }
            ]
          },
          {
            text: [
              {
                text: 'Repayment 1\n',
                style: 'label'
              },
              {
                text: [
                  {
                    text: productSplit?.repayment1.formatWithCurrency(
                      $rootScope.CurrentCurrency(),
                      0
                    ),
                    style: 'value'
                  },
                  {
                    text: ` ${productSplit?.repayment1Info || '-'}`,
                    style: 'valueXS'
                  }
                ]
              }
            ]
          }
        ]);
      }
      if (productSplit?.repayment2) {
        if (organizationCountry?.cca2 === 'NZ') {
          content[content.length - 1].table.body[1][3] = {
            text: [
              {
                text: 'Repayment 2\n',
                style: 'label'
              },
              {
                text: [
                  {
                    text: productSplit?.repayment2.formatWithCurrency(
                      $rootScope.CurrentCurrency(),
                      0
                    ),
                    style: 'value'
                  },
                  {
                    text: ` ${productSplit?.repayment2Info || '-'}`,
                    style: 'valueXS'
                  }
                ]
              }
            ]
          };
        } else {
          content[content.length - 1].table.body.push([
            {
              text: [
                {
                  text: 'Repayment 2\n',
                  style: 'label'
                },
                {
                  text: [
                    {
                      text: productSplit?.repayment2.formatWithCurrency(
                        $rootScope.CurrentCurrency(),
                        0
                      ),
                      style: 'value'
                    },
                    {
                      text: ` ${productSplit?.repayment2Info || '-'}`,
                      style: 'valueXS'
                    }
                  ]
                }
              ]
            },
            '',
            '',
            ''
          ]);
        }
      }
      if (productSplit?.repayment3) {
        if (organizationCountry?.cca2 === 'NZ') {
          content[content.length - 1].table.body.push([
            {
              text: [
                {
                  text: 'Repayment 3\n',
                  style: 'label'
                },
                {
                  text: [
                    {
                      text: productSplit?.repayment3.formatWithCurrency(
                        $rootScope.CurrentCurrency(),
                        0
                      ),
                      style: 'value'
                    },
                    {
                      text: ` ${productSplit?.repayment3Info || '-'}`,
                      style: 'valueXS'
                    }
                  ]
                }
              ]
            },
            '',
            '',
            ''
          ]);
        } else {
          content[content.length - 1].table.body[2][1] = {
            text: [
              {
                text: 'Repayment 3\n',
                style: 'label'
              },
              {
                text: [
                  {
                    text: productSplit?.repayment3.formatWithCurrency(
                      $rootScope.CurrentCurrency(),
                      0
                    ),
                    style: 'value'
                  },
                  {
                    text: ` ${productSplit?.repayment3Info || '-'}`,
                    style: 'valueXS'
                  }
                ]
              }
            ]
          };
        }
      }
    }
    return content;
  };

  /**
   * Prepare preferred product details.
   *
   * @param {Array} lenders
   * @param {Object} preferredProduct
   * @param {Object} loan
   * @param {Object} workflow
   *
   * @returns {Object}
   */
  const preparePreferredProductDetails = ({ lenders, preferredProduct, loan, workflow }) => {
    const content = [];
    if (preferredProduct) {
      content.push({
        text: '',
        margin: [0, 20, 0, 0]
      });
      if (workflow?.isAssetFinance()) {
        content.push(
          prepareAssetFinancePreferredProductDetails({ lenders, preferredProduct, loan })
        );
      }
      if (workflow?.isHomeLoan()) {
        content.push(
          prepareHomeLoanPreferredProductDetails({ lenders, preferredProduct, loan })
        );
      }
    }
    return content;
  };

  return {
    preparePreferredProductDetails
  };
};
